/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { Fragment, useContext } from 'react';
import { List } from 'antd';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import { FontAwesomeIconProps } from '@fortawesome/react-fontawesome';
import { IconPrefix } from '@fortawesome/pro-solid-svg-icons';
import { useIntl } from 'react-intl';
import { TableEntry } from '~components/atoms/FileTables/FileTable.utils';
import IconButton from '~components/_common/IconButton/IconButton';
import { DB_STATE } from '~src/types/enums';
import FileVersionsContext from '~src/contexts/FileVersionsContext';
import { formatBytes } from '~src/utils/formatBytes';
import DotMenu from '../DotMenu/DotMenu';
import dataContextMenu from '~src/data/dataContextMenu';
import { useItemPermission } from '~src/hooks/useItemPermission';
import { formatDate } from '~src/utils/formatDate';
import { SVFIconType } from '~components/atoms/Viewers/SVFViewer/components/SVFIconType';
import { useFileViewerContext } from '~src/contexts/FileToViewContext';
import { message } from 'antd';

interface FileListItemProps {
  item: TableEntry;
  onClick: () => void;
}

export const FileListItem: React.FC<FileListItemProps> = ({
  item,
  onClick,
}) => {
  const { Item } = List;
  const intl = useIntl();
  const styles = useStyles(styleFn);
  const { fileVersionsContext } = useContext(FileVersionsContext);
  const { setFileToView } = useFileViewerContext();

  const dotMenuPermissions = useItemPermission({
    folder: item.dbFolder,
    file: item.dbFile,
  });

  const getUploadedDate = () => {
    if (item.folder) {
      return formatDate(item.createdAt, 'long');
    } else if (item.dbFile && item.dbFile.versions) {
      const date = fileVersionsContext[item.id]
        ? (fileVersionsContext[item.id]?.created_at as number)
        : (item.dbFile.versions[0]?.created_at as number);
      return formatDate(date, 'long');
    } else {
      return '-';
    }
  };

  const getFileSize = () => {
    if (item.folder) {
      return '';
    } else if (fileVersionsContext[item.id]) {
      return formatBytes(fileVersionsContext[item.id]?.size || 0, 1);
    } else if (item.dbFile && item.dbFile.versions) {
      return formatBytes(item.dbFile.versions[0]?.size || 0, 1);
    } else {
      return '-';
    }
  };

  // short name for mobile: first 17 chars + "..." + extension (if base > 20)
  const fullName = item.name || '';
  const dotIdx = fullName.lastIndexOf('.');
  const extPart = dotIdx > 0 ? fullName.slice(dotIdx) : '';
  const basePart = dotIdx > 0 ? fullName.slice(0, dotIdx) : fullName;
  const displayName =
    basePart.length > 60 ? `${basePart.slice(0, 57)}...${extPart}` : fullName;

  // 3D files detection variables
  const ext = (item.extension || '').toLowerCase();
  const isCad = ['rvt', 'dwg', 'ifc', 'nwd'].includes(ext);
  const version =
    fileVersionsContext[item.id] ?? (item.dbFile?.versions?.[0] as any);
  const state =
    (version?.extension_svf_model?.state as number | undefined) ?? undefined;
  const isViewable = state === 7;

  return (
    <Fragment>
      <Item
        style={{
          padding: '20px 20px',
          background: isViewable ? 'rgba(34,248,28,0.13)' : undefined, // light green
        }}>
        <div css={styles.itemContainer}>
          {/* LEFT ICONS BLOCK: main file icon + optional SVF 3D icon */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {(() => {
              if (isCad) {
                // Use context version or mobile row's first version (has extension_svf_model)
                return (
                  // Stack icons vertically: top = Autodesk (only if green), bottom = base CAD
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 2,
                    }}>
                    {/* TOP: Autodesk icon (mobileView hides non-green automatically) */}
                    <SVFIconType
                      key={`svf-top-${item.id}-${state ?? 'none'}`}
                      version={version}
                      extension={item.extension}
                      variant="sidebar"
                      mobileView={true} // hide grey/blue on mobile, show only green
                      disabled={!isViewable}
                      customTooltip={
                        isViewable
                          ? intl.formatMessage({ id: 'svf-view-file-window' })
                          : intl.formatMessage({ id: 'svf-view-file' })
                      }
                      onClick={() => {
                        // Mobile: open SVFViewer directly (no modal, no transcribe)
                        if (isViewable && item.dbFile) {
                          setFileToView({ ...item.dbFile });
                        }
                      }}
                      type={item.type}
                    />

                    {/* BOTTOM: base blue CAD icon (always visible) */}
                    <div
                      style={{
                        transform: isViewable ? 'scale(1)' : 'scale(1.25)',
                        transformOrigin: 'center',
                      }}>
                      <SVFIconType
                        key={`file-icon-${item.id}`}
                        version={version}
                        extension={item.extension}
                        variant="table"
                        customTooltip={intl.formatMessage(
                          { id: item.type.icon },
                          { type: item.extension?.toUpperCase() }
                        )}
                        onClick={() => onClick()}
                        type={item.type}
                      />
                    </div>
                  </div>
                );
              }

              // Non-CAD files keep their default icon
              return (
                <IconButton
                  click={() => onClick()}
                  iconStyle={item.type.iconStyle as IconPrefix}
                  icon={item.type.icon as FontAwesomeIconProps['icon']}
                  customStyle={styles.icons}
                  color={
                    item.state === DB_STATE.REMOVED ? 'red' : item.type.color
                  }
                  tooltip={intl.formatMessage(
                    { id: item.type.icon },
                    { type: item.extension?.toUpperCase() }
                  )}
                  badge={item.type.is_empty ? '0' : undefined}
                  backgroundOff
                />
              );
            })()}
          </div>
          <div css={styles.textContainer} onClick={onClick}>
            <div
              css={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                minHeight: 30,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                wordBreak: 'break-all',
                color: isViewable ? '#1cbd22' : undefined,
              }}>
              {displayName}
              {item.favourite && (
                <IconButton
                  icon={['fas', 'star']}
                  color="yellow"
                  backgroundOff
                />
              )}{' '}
            </div>
            {item?.description?.length > 0 && (
              <div style={{ color: 'grey' }}>
                <span style={{ fontWeight: 'bold' }}>
                  {intl.formatMessage({ id: 'description' })}:
                </span>{' '}
                <span
                  style={{
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    overflowWrap: 'anywhere',
                  }}>
                  {item.description}
                </span>
              </div>
            )}
            <div>
              <span css={styles.infoText}>{getUploadedDate()} </span>
              <span css={styles.infoText}>{getFileSize()}</span>
            </div>
          </div>
          <DotMenu
            item={item}
            menuItems={
              item.folder
                ? dataContextMenu.folderMobile
                : dataContextMenu.fileMobile
            }
            permissionLevel={dotMenuPermissions}
          />
        </div>
      </Item>
    </Fragment>
  );
};

const styleFn: ThemedStyleFn = (theme) => ({
  itemContainer: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    alignItems: 'center',
    position: 'relative',
  },
  textContainer: {
    paddingLeft: '20px',
    width: '80%',
  },
  icons: {
    color: theme.colors.secondary,
    fontSize: '30px',
  },

  infoText: {
    color: theme.colors.lightGray,
  },
});
